<script setup lang="ts">
import { tracking } from '~/services/tracking.client'

import {computed, onBeforeUnmount, onMounted, ref} from 'vue'

interface BannerSlot {
    id: string
    label: string
    web: string
    mobile: string
    url: string
    fit: string
}

interface BannerInterface {
    bannerHeight: number
    bannerHeightMobile: number
    slots: BannerSlot[]
}

const props = defineProps<{
    config: BannerInterface,
    class?:string,
    id?:string|number
}>()

const main = computed(() =>
    props.config.slots?.filter((i) => i.id.includes('main-') && (i.web || i.mobile)) || []
)


const hasCarousel = computed(() => main.value.length > 0)

const currentIndex = ref(0)

const nextSlide = () => {
    if (!hasCarousel.value) return
    currentIndex.value = (currentIndex.value + 1) % main.value.length
}
const prevSlide= () => {
  if (!hasCarousel.value) return
  currentIndex.value = currentIndex.value - 1 > -1 ? currentIndex.value - 1:main.value.length-1;
}
const goToSlide = (index: number) => {
    if (!hasCarousel.value) return
    currentIndex.value = index

    if (main.value.length > 1) {
        stopAutoPlay()
        startAutoPlay()
    }
}


let intervalId: number | undefined

const startAutoPlay = () => {
    if (main.value.length <= 1) return
    stopAutoPlay()
    intervalId = window.setInterval(nextSlide, 5000)
}

const stopAutoPlay = () => {
    if (intervalId) {
        clearInterval(intervalId)
        intervalId = undefined
    }
}

onMounted(startAutoPlay)
onBeforeUnmount(stopAutoPlay)
const promotionRoot = ref<HTMLElement>()
let promotionObserver: IntersectionObserver | undefined
onMounted(() => {
    if (!promotionRoot.value || !('IntersectionObserver' in window)) return
    const seen = new Set<string>()
    promotionObserver = new IntersectionObserver(entries => {
        for (const entry of entries) {
            const target = entry.target as HTMLElement
            const id = target.dataset.promotionId
            if (entry.isIntersecting && entry.intersectionRatio >= 0.5 && id && !seen.has(id)) {
                seen.add(id)
                tracking.promotion('view_promotion', id, target.dataset.promotionName || id)
            }
        }
    }, { threshold: 0.5 })
    promotionRoot.value.querySelectorAll('[data-promotion-id]').forEach(element => promotionObserver!.observe(element))
})
onBeforeUnmount(() => promotionObserver?.disconnect())
</script>

<template>
    <div
      role="banner" ref="promotionRoot"
            class="banner3"
            :class="props?.class || ''"
          :style="'--banner-height:'+config.bannerHeight+'; --banner-height-mobile: '+config.bannerHeightMobile+';'"
    >
        <!-- LEFT: big carousel -->
        <div v-if="hasCarousel" class="banner3__left" >
            <div class="banner3__slides">
                <div
                        class="banner3__track"
                        :style="{ transform: `translateX(-${currentIndex * 100}%)` }"
                >
                    <NuxtLinkLocale
                            v-for="(item, index) in main"
                            :key="item.id"
                            :to="item.url || '/'" :data-promotion-id="`${props.id || 'banner'}:${item.id}`" :data-promotion-name="item.label" @click="tracking.promotion('select_promotion', `${props.id || 'banner'}:${item.id}`, item.label)"
                            class="banner3__slide"
                    >
                        <picture>
                            <source
                                    v-if="item.mobile"
                                    :srcset="item.mobile"
                                    media="(max-width: 992px)"
                            >
                            <source
                                    v-if="item.web"
                                    :srcset="item.web"
                                    media="(min-width: 993px)"
                            >
                          <img
                            class="banner3__img"
                            :src="item.web || item.mobile"
                            :alt="item.label"
                            :style="{ objectFit: item.fit || 'contain' }"
                            :loading="index === 0 ? 'eager' : 'lazy'"
                            :fetchpriority="index === 0 ? 'high' : undefined"
                            decoding="async"
                          >
                        </picture>
                    </NuxtLinkLocale>
                </div>
            </div>

            <!-- dots -->
          <div v-if="main.length > 1" class="banner3__dots">
            <button
                v-for="(item, index) in main"
                :key="item.id"
                type="button"
                class="banner3__dot"
                :class="{ 'banner3__dot--active': index === currentIndex }"
                @click="goToSlide(index)"
            />
          </div>
          <!-- arrows -->
          <div v-if="main.length > 1">
            <button
                type="button"
                class="banner3__arrow banner3__arrow-left"
                @click="prevSlide">
              <icon name="material-symbols-light:arrow-back-ios-new"></icon>
            </button>
            <button
                type="button"
                class="banner3__arrow banner3__arrow-right"
                @click="nextSlide">
              <icon name="material-symbols-light:arrow-forward-ios"></icon>
            </button>
          </div>
        </div>


    </div>
</template>

<style lang="scss" scoped>
.banner3 {
  display: flex;
  gap: 16px;
  height: var(--banner-height);
  width: 100%;
  overflow: hidden;
  @media (max-width: 992px) {
    padding-left:0;
    padding-right:0;
  }
  &:hover {
    .banner3__arrow {
      opacity: 1 !important;
    }
  }
}

/* Ляво: голямо карусел изображение */
.banner3__left {
  position: relative;
  flex: 2 1 0;
  height: 100%;
  overflow: hidden;
}

.banner3__slides {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}
.banner3__track {
  display: flex;
  height: 100%;
  width: 100%;
  transition: transform 0.6s ease;
}

.banner3__slide {
  flex: 0 0 100%;
  height: 100%;
}

.banner3__img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  @media (max-width: 992px) {
    object-position: center;
    object-fit: contain !important;
  }
}

/* Dots */
.banner3__dots {
  position: absolute;
  bottom: 12px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 6px;
}

.banner3__dot {
  all: unset;
  display: block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.5);
  cursor: pointer;

  &--active {
    background: rgba(255, 255, 255, 1);
  }
}

/* Дясно: две картинки една над друга */
.banner3__right {
  flex: 1 1 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.banner3__side {
  flex: 1;
  position: relative;
  overflow: hidden;
  display: block;

  .banner3__img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

/* Mobile: само карусела, full-width */
@media (max-width: 992px) {
  .banner3 {
    height: var(--banner-height-mobile);
  }

  .banner3__right {
    display: none;
  }

  .banner3__left {
    flex: 1 1 100%;
    height: 100%;
  }
}
.banner3__arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--el-color-primary);
  border-radius: 50%;
  color: #fff;
  font-size: 22px;
  opacity: 0;
  cursor: pointer;
  border: none;
  @media (max-width: 992px) {
    display: none;
  }
}

.banner3__arrow-left {
  left: 20px;
}

.banner3__arrow-right {
  right: 20px;
}

.banner3__arrow:hover {
  background: var(--el-color-primary);
}
</style>




